/**
 * Latest-year capital-allocation snapshot (ADR-0035). Facts only — display
 * formatting lives in the UI. Missing is null, never 0 (R2b).
 */
import type { CashFlowPeriod } from '@meridian/contracts';

import { latestServablePeriod } from './statement-ratios';

export interface CapitalAllocation {
  readonly year: number | null;
  readonly capex: number | null;
  readonly dividendsPaid: number | null;
  readonly buybacks: number | null;
  readonly debtRepaid: number | null;
  readonly debtIssued: number | null;
  readonly netChangeCash: number | null;
}

const EMPTY: CapitalAllocation = {
  year: null,
  capex: null,
  dividendsPaid: null,
  buybacks: null,
  debtRepaid: null,
  debtIssued: null,
  netChangeCash: null,
};

function isFiniteMetric(value: number | null): boolean {
  return value !== null && Number.isFinite(value);
}

function allocationServable(period: CashFlowPeriod): boolean {
  return (
    isFiniteMetric(period.capex) ||
    isFiniteMetric(period.dividendsPaid) ||
    isFiniteMetric(period.buybacks) ||
    isFiniteMetric(period.debtRepaid) ||
    isFiniteMetric(period.debtIssued) ||
    isFiniteMetric(period.netChangeCash)
  );
}

export function capitalAllocation(periods: readonly CashFlowPeriod[]): CapitalAllocation {
  const latest = latestServablePeriod(periods, allocationServable);
  if (latest === undefined) return EMPTY;
  return {
    year: latest.year,
    capex: latest.capex,
    dividendsPaid: latest.dividendsPaid,
    buybacks: latest.buybacks,
    debtRepaid: latest.debtRepaid,
    debtIssued: latest.debtIssued,
    netChangeCash: latest.netChangeCash,
  };
}

export function hasCapitalAllocation(snapshot: CapitalAllocation): boolean {
  return (
    isFiniteMetric(snapshot.capex) ||
    isFiniteMetric(snapshot.dividendsPaid) ||
    isFiniteMetric(snapshot.buybacks) ||
    isFiniteMetric(snapshot.debtRepaid) ||
    isFiniteMetric(snapshot.debtIssued) ||
    isFiniteMetric(snapshot.netChangeCash)
  );
}
